import React, { useMemo, useState } from 'react';
import { Joyride, STATUS, type EventData, type Step } from 'react-joyride';
import { Map } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { useSession } from '../lib/session';
import { useLocation } from '../lib/location';
import { useEvents3Day } from '../hooks/useEvents';
import { useReservations3Day } from '../hooks/useReservations';
import { DayColumn, sod, eod, eventOnDay } from '../components/EventCard';
import '../styles/home.css';

const TOUR_STEPS: Step[] = [
  {
    target: '#home-header',
    title: 'Your daily briefing',
    content: 'Your personalized greeting, today\'s date, and a quick reminder of what to check before you hit the floor.',
    placement: 'bottom',
  },
  {
    target: '#home-events-section',
    title: 'Upcoming events',
    content: 'See what\'s happening over the next 3 days — concerts, sporting events, festivals, and more — so you can prep for busy shifts.',
    placement: 'bottom',
  },
  {
    target: '#home-handoff-section',
    title: 'Handoff notes',
    content: 'Notes left by the previous shift, with pinned items at the top. Watch for tap issues, keg kicks, and guest flags.',
    placement: 'right',
  },
  {
    target: '#home-timeslip-section',
    title: "Today's timeslip",
    content: 'Track your shift hours here. Submit at the end of the night and check back to see the approval status.',
    placement: 'left',
  },
];

interface HandoffNote {
  id: string;
  author: string;
  role: string;
  timestamp: string;
  body: string;
  pinned: boolean;
  tags: string[];
}

const SEED_NOTES: HandoffNote[] = [
  {
    id: 'note-1',
    author: 'Mara K.',
    role: 'Closing bartender',
    timestamp: new Date(Date.now() - 8.2 * 3600 * 1000).toISOString(),
    body: 'Tap 4 (Elysian Fields) is foaming hard — pressure feels off. Pulled it from the rec list around 9pm. Worth checking before open.',
    pinned: true,
    tags: ['line-issue', 'tap-4'],
  },
  {
    id: 'note-2',
    author: 'Jules P.',
    role: 'Closing bartender',
    timestamp: new Date(Date.now() - 8.2 * 3600 * 1000).toISOString(),
    body: "Big party of 12 at table 7 worked through most of the Cerberus sixtel. Kept it to 2 per person but a couple folks tried to bar-hop the rule. Heads up if they're back.",
    pinned: false,
    tags: ['guest-note'],
  },
  {
    id: 'note-3',
    author: 'Mara K.',
    role: 'Closing bartender',
    timestamp: new Date(Date.now() - 8.1 * 3600 * 1000).toISOString(),
    body: 'Lethe kicked at 6:40. Left tap 6 capped, did NOT clean the line — next keg of Lethe is dropping Friday. Flag for Dan.',
    pinned: true,
    tags: ['kicked', 'tap-6'],
  },
  {
    id: 'note-4',
    author: 'Dan R.',
    role: 'Manager',
    timestamp: new Date(Date.now() - 0.75 * 3600 * 1000).toISOString(),
    body: 'CO2 tank #2 swapped this morning. New one is at 1800 psi. Glycol temp is 34°F as of 7am, all good.',
    pinned: false,
    tags: ['ops'],
  },
  {
    id: 'note-5',
    author: 'Jules P.',
    role: 'Closing bartender',
    timestamp: new Date(Date.now() - 8.1 * 3600 * 1000).toISOString(),
    body: 'Reminder: Beatrice (Italian Pils) drops this week — we should taste it as a team before service Friday.',
    pinned: false,
    tags: ['training'],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hoursAgo(iso: string): string {
  const now = new Date();
  const d = new Date(iso);
  const h = Math.round((now.getTime() - d.getTime()) / 36e5);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function todayLabel(): string {
  return new Date().toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}

function firstToken(displayName: string): string {
  return displayName.split(' ')[0] ?? displayName;
}

// ---------------------------------------------------------------------------
// Events gallery (3-day strip)
// ---------------------------------------------------------------------------

function EventsGallery() {
  const { locationId } = useLocation();
  const { data, isLoading, error } = useEvents3Day(locationId);
  const { data: resData } = useReservations3Day(locationId);
  const now = new Date();
  const days = useMemo(() => {
    return Array.from({ length: 3 }, (_, i) => {
      const d = new Date(now);
      d.setDate(d.getDate() + i);
      return { date: d, start: sod(d), end: eod(d) };
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return <div className="home-day-empty">Could not load events.</div>;
  }

  const reservations = resData?.items ?? [];

  return (
    <div className="home-events-gallery">
      {days.map((d, i) => {
        const events = (data?.items ?? [])
          .filter((ev) => eventOnDay(ev, d.start, d.end))
          .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
        const resCount = reservations.filter((r) => {
          const t = new Date(r.reservedAt).getTime();
          return t >= d.start.getTime() && t <= d.end.getTime();
        }).length;
        return (
          <div key={i} className="home-events-snap">
            <DayColumn
              day={d.date}
              events={events}
              reservationCount={resCount}
              isToday={i === 0}
              isLoading={isLoading}
            />
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard page
// ---------------------------------------------------------------------------

export function Home(): React.ReactElement {
  const { user } = useSession();
  const navigate = useNavigate();
  const [tourRunning, setTourRunning] = useState(false);

  function handleTourEvent(data: EventData) {
    if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
      setTourRunning(false);
    }
  }

  const recentNotes = useMemo(() => {
    const cutoff = new Date(Date.now() - 24 * 3600 * 1000);
    return [...SEED_NOTES]
      .filter(n => new Date(n.timestamp) >= cutoff)
      .sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });
  }, []);

  const timeslip = {
    status: 'draft',
    blockCount: 5,
    bartenderCount: 5,
    myScheduledLabel: '6:00 PM – 2:00 AM',
  };

  return (
    <div className="home-page">
      <Joyride
        steps={TOUR_STEPS}
        run={tourRunning}
        continuous
        scrollToFirstStep
        onEvent={handleTourEvent}
        options={{
          primaryColor: '#853c1e',
          showProgress: true,
          skipBeacon: true,
          buttons: ['back', 'close', 'primary', 'skip'],
          zIndex: 10000,
        }}
      />

      {/* Header */}
      <div id="home-header" className="home-header">
        <div className="uppercase-label home-header-date">{todayLabel()}</div>
        <h1 className="home-title">
          {greeting()}, {firstToken(user.displayName)}.
        </h1>
        <div className="home-subtitle">
          Here's what to know before you hit the floor.
        </div>
        <button
          className="home-tour-btn"
          onClick={() => setTourRunning(true)}
          aria-label="Start page tour"
        >
          <Map size={14} />
          Take a tour
        </button>
      </div>

      {/* Events strip */}
      <section id="home-events-section" className="home-section">
        <div className="home-section-header">
          <h2 className="home-section-title">Next 3 days</h2>
          <button className="home-link-btn" onClick={() => navigate('/events')}>
            View all events →
          </button>
        </div>
        <EventsGallery />
      </section>

      {/* Two-column: handoff notes + timeslip */}
      <div className="home-two-col">

        {/* Handoff notes */}
        <section id="home-handoff-section">
          <div className="home-section-header">
            <h2 className="home-section-title">Handoff notes · last 24 hours</h2>
            <button className="home-link-btn" onClick={() => navigate('/handoff')}>
              Open handoff →
            </button>
          </div>
          <div className="home-notes-list">
            {recentNotes.length === 0 ? (
              <div className="home-notes-empty">
                No handoff notes in the last 24 hours.
              </div>
            ) : (
              recentNotes.map(n => (
                <div key={n.id} className={`home-note${n.pinned ? ' home-note--pinned' : ''}`}>
                  <div className="home-note-header">
                    <div className="home-note-author">
                      {n.pinned && <Icon name="pin" size={12} />}
                      <strong>{n.author}</strong>
                      <span className="home-note-role">{n.role}</span>
                    </div>
                    <span className="home-note-time">{hoursAgo(n.timestamp)}</span>
                  </div>
                  <p className="home-note-body">{n.body}</p>
                  {n.tags.length > 0 && (
                    <div className="home-note-tags">
                      {n.tags.map(t => (
                        <span key={t} className="home-note-tag">#{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </section>

        {/* Timeslip card */}
        <section id="home-timeslip-section">
          <h2 className="home-timeslip-title">Today's timeslip</h2>
          <button className="home-timeslip-btn" onClick={() => navigate('/timeslip')}>
            <div className="home-timeslip-top">
              <span className="home-timeslip-badge">DRAFT</span>
              <Icon name="clock" size={16} />
            </div>
            <div>
              <div className="home-timeslip-status-title">Not yet submitted</div>
              <div className="home-timeslip-meta">
                {timeslip.bartenderCount} bartenders · {timeslip.blockCount} blocks · pools not entered
              </div>
            </div>
            <div className="home-timeslip-footer">
              <span>Your scheduled shift</span>
              <span className="mono home-timeslip-shift">{timeslip.myScheduledLabel}</span>
            </div>
            <div className="home-timeslip-cta">
              Open timeslip <Icon name="arrow-right" size={14} />
            </div>
          </button>
        </section>

      </div>
    </div>
  );
}
